import { useCallback, useEffect, useState } from 'react';
import { Warning } from '@phosphor-icons/react';
import {
  direQueCEstLu,
  lireMesAvertissements,
  type Avertissement,
} from '../../firebase/avertissements';
import { useSession } from '../../store/SessionContext';
import { t } from '../../i18n';

/**
 * L'avertissement, montre a celui qui l'a recu.
 *
 * C'est le seul geste de moderation qui parle au membre. Il se dresse
 * donc devant l'application plutot que de se poser dans un coin : un
 * avertissement qu'on peut balayer sans le lire n'avertit personne.
 *
 * Une seule issue, et elle demande un geste. Ce geste s'ecrit dans la
 * base — c'est ce qui distingue « on l'a prevenu » de « on a ecrit
 * quelque part », et c'est la seule chose que le membre ait le droit
 * d'ecrire dans cette branche.
 *
 * Ils s'affichent un par un, du plus ancien au plus recent : deux
 * reproches empiles dans le meme cadre se liraient comme un seul.
 *
 * La question se repose au retour au premier plan, comme pour la porte du
 * consentement : une application de telephone vit des jours sans etre
 * relancee, et un avertissement pose ce matin attendrait sinon la
 * semaine prochaine.
 */
export function AvertissementRecu() {
  const { user } = useSession();
  const [attente, setAttente] = useState<Avertissement[]>([]);
  const [envoi, setEnvoi] = useState(false);

  const regarder = useCallback(() => {
    if (!user) return setAttente([]);
    // Un echec de lecture ne montre rien : mieux vaut un avertissement vu
    // en retard qu'un ecran d'erreur devant toute l'application.
    void lireMesAvertissements()
      .then(setAttente)
      .catch(() => undefined);
  }, [user]);

  useEffect(() => {
    regarder();
    document.addEventListener('visibilitychange', regarder);
    return () => document.removeEventListener('visibilitychange', regarder);
  }, [regarder]);

  const premier = attente[0];
  if (!premier) return null;

  const compris = () => {
    setEnvoi(true);
    void direQueCEstLu(premier.id)
      .then(() => setAttente((reste) => reste.slice(1)))
      // Sans accuse de lecture, il reviendra a la prochaine ouverture.
      // C'est le bon comportement : il n'a pas ete prevenu.
      .catch(() => undefined)
      .finally(() => setEnvoi(false));
  };

  return (
    <div className="confirm-root">
      {/* Pas de voile cliquable : on ne ferme pas ceci en tapant a cote. */}
      <div className="confirm__scrim" />
      <div className="confirm confirm--avertissement" role="dialog" aria-modal="true">
        <span className="confirm__marque" aria-hidden="true">
          <Warning size={26} weight="fill" />
        </span>
        <p className="confirm__titre">{t.moderation.avertissement.titre}</p>
        <p className="confirm__texte">{premier.texte}</p>
        <div className="confirm__actions">
          <button className="confirm__yes" disabled={envoi} onClick={compris}>
            {t.moderation.avertissement.compris}
          </button>
        </div>
      </div>
    </div>
  );
}
